// Переказ з картки користувача: списання + транзакція у виписці, а якщо
// отримувач — користувач застосунку, то ще й зарахування на його картку з
// вхідною транзакцією. Шляхи й формат — як у бота (bot.js): основна картка
// — balance/{chatId} { value } і transactions/{chatId}, додаткові —
// cards/{chatId}/{cardId}/balance і cardTransactions/{chatId}/{cardId}.
import { ref, get, push, remove, runTransaction } from 'firebase/database';
import { database } from './firebaseConfig';
import { MAIN_CARD_ID } from './cardsStore';
import { shortName } from './cardLookup';

const isMain = (cardId) => !cardId || cardId === MAIN_CARD_ID;
export const balancePath = (chatId, cardId) => (isMain(cardId) ? `balance/${chatId}/value` : `cards/${chatId}/${cardId}/balance`);
export const txPath = (chatId, cardId) => (isMain(cardId) ? `transactions/${chatId}` : `cardTransactions/${chatId}/${cardId}`);

const TRANSFER_IMG = 'https://monotest-d5389.web.app/icons-bot/transfer-logo.png';
const INCOME_IMG = 'https://monotest-d5389.web.app/icons-bot/cat-ico.png';

// delta < 0 — списання; не пускаємо в мінус (allowNegative — для повернення
// скасованого переказу: отримувач міг уже витратити гроші). Повертає false, якщо коштів
// не вистачило.
// null у обробнику — або значення ще не завантажено, або балансу в БД справді
// немає (користувач ні разу не вводив баланс). Зарахування рахуємо від 0: якщо
// насправді значення є, Firebase побачить розбіжність і перезапустить обробник
// з реальним. Списання з null не робимо — віддаємо null (Firebase так само
// перезапустить, а якщо балансу справді нема — коштів немає).
export async function adjustBalance(chatId, cardId, delta, allowNegative = false) {
  let insufficient = false;
  const res = await runTransaction(ref(database, balancePath(chatId, cardId)), (cur) => {
    if (cur === null && delta < 0) return null;
    const next = parseFloat(cur || 0) + delta;
    if (next < 0 && !allowNegative) { insufficient = true; return undefined; }
    insufficient = false;
    return next.toFixed(2);
  });
  return res.committed && !insufficient && res.snapshot.val() !== null;
}

// recipient — з findUserByCard ({ chatId, cardId, name, photo }) або null
// для картки іншого банку. Кидає Error('insufficient_funds').
// Повертає дані для екрана успіху й cancelTransfer.
export async function sendTransfer({
  chatId, fromCardId, amount, cardNumber, recipient, comment,
}) {
  const value = Math.round(amount * 100) / 100;
  if (!(await adjustBalance(chatId, fromCardId, -value))) throw new Error('insufficient_funds');

  const now = new Date().toISOString();
  const outgoing = {
    id: Date.now(),
    name: recipient ? recipient.name : cardNumber.replace(/(\d{4})(?=\d)/g, '$1 '),
    img: (recipient && recipient.photo) || TRANSFER_IMG,
    amount: `-${value.toFixed(2)}`,
    date: now,
    type: 'expense',
    category: 'Transfer',
    cardNumber,
  };
  if (recipient) outgoing.bank = 'mono';
  if (comment) outgoing.comment = comment;
  const outRef = await push(ref(database, txPath(chatId, fromCardId)), outgoing);
  const result = {
    chatId, fromCardId, value, cardNumber, recipient, tx: outgoing,
    outPath: `${txPath(chatId, fromCardId)}/${outRef.key}`,
  };

  if (!recipient) return result;
  const sender = (await get(ref(database, `users/${chatId}`))).val() || {};
  // На валютну картку не конвертуємо — гривні йдуть на основну
  const toCard = recipient.currency && recipient.currency !== 'UAH' ? MAIN_CARD_ID : recipient.cardId;
  await adjustBalance(recipient.chatId, toCard, value);
  const incoming = {
    id: Date.now() + 1,
    name: `Вiд: ${shortName(sender.pib) || 'monobank'}`,
    img: sender.photoUrl || INCOME_IMG,
    amount: value.toFixed(2),
    date: now,
    type: 'income',
    category: 'ADD_TRANSFER',
  };
  if (comment) incoming.comment = comment;
  const inRef = await push(ref(database, txPath(recipient.chatId, toCard)), incoming);
  return { ...result, toCard, inPath: `${txPath(recipient.chatId, toCard)}/${inRef.key}` };
}

// «Скасувати платіж» — повертаємо все як було: гроші відправнику,
// списуємо в отримувача, прибираємо обидві транзакції
export async function cancelTransfer(t) {
  await adjustBalance(t.chatId, t.fromCardId, t.value, true);
  await remove(ref(database, t.outPath));
  if (!t.recipient) return;
  await adjustBalance(t.recipient.chatId, t.toCard, -t.value, true);
  await remove(ref(database, t.inPath));
}
